import { useQueryClient } from '@tanstack/react-query';

import { useMutation } from '@hooks/reactQuery';

import { useAppDispatch } from '@store/store';

import { setSnackbar } from '@slices/common';

import {
	attachPortalFiles,
	downloadPortalFile,
	downloadPortalFiles,
	movePortalFiles,
	removePortalFiles,
	uploadFiles as uploadFilesApi,
} from '@api/portalFiles';

import { fileSaver } from '@utils/fileSaver';
import { handleErrorFromMessage } from '@utils/handleRequestError';

import { IGroupOperationWithActionDTO } from '@interfaces/common/defaultDTO';
import { IResponseError } from '@interfaces/errors/apiErrors';
import {
	IAttachPortalFileDTO,
	IDeletePortalFilesDTO,
	IDownloadPortalFileDTO,
	IDownloadPortalFilesDTO,
	IGroupOperationAttachPortalDTO,
	IMovePortalFilesDTO,
	IUploadFilesDTO,
} from '@interfaces/portal/dto/foldersManagementDTO';

import { EMenuActionsTypes, ESnackbarTypes, EUserRoles } from '@enums/common';

import {
	ATTACH_PORTAL_FILES,
	DELETE_PORTAL_FILES,
	DOWNLOAD_PORTAL_FILES,
	GET_PORTAL_FILES,
	GET_PORTAL_FOLDERS,
	GET_PORTAL_UPLOAD_FILES,
	PORTAL_MOVE_FILES,
} from '@constants/queryKeys';
import { responseMessages } from '@constants/responseMessages';

export interface IUseMutatePortalFilesReturned {
	uploadFiles: (data: IUploadFilesDTO) => Promise<void>;
	isUploadingFiles: boolean;
	handleDeletePortalFiles: (data: IGroupOperationWithActionDTO) => void;
	isLoadingPortalDeleteFiles: boolean;
	handleDeletePortalFile: (id: string) => void;
	handleDownloadPortalFiles: (data: IGroupOperationWithActionDTO) => void;
	isLoadingPortalDownloadFiles: boolean;
	handleDownloadPortalFile: (data: IDownloadPortalFileDTO) => void;
	moveFiles: (data: IMovePortalFilesDTO) => void;
	isLoadingMoveFiles: boolean;
	handleAttachFilesToMilestones: (data: IGroupOperationAttachPortalDTO) => void;
	isLoadingAttachFilesToMilestones: boolean;
	handleAttachFileToMilestones: (data: IAttachPortalFileDTO) => void;
}

interface IUseMutatePortalFiles {
	folderId?: string;
	searchValue?: string;
	closeDeleteConfirm?: () => void;
}

export function useMutatePortalFiles ({
	folderId,
	searchValue,
	closeDeleteConfirm,
}: IUseMutatePortalFiles): IUseMutatePortalFilesReturned {
	const queryClient = useQueryClient();
	const dispatch = useAppDispatch();

	const {
		mutateAsync: uploadFiles,
		isLoading: isUploadingFiles,
	} = useMutation<IUploadFilesDTO>({
		mutationFn: uploadFilesApi,
		mutationKey: [GET_PORTAL_UPLOAD_FILES],
		onSuccess: (_, variables) => {
			dispatch(setSnackbar({
				message: responseMessages.uploadPortalFiles(variables.folderName),
				type: ESnackbarTypes.success,
			}));

			return queryClient.invalidateQueries([GET_PORTAL_FILES]);
		},
		allowedRoles: [EUserRoles.portalProjectAdministrator, EUserRoles.portalProjectMember],
		onError: () => {
			dispatch(setSnackbar({
				message: responseMessages.error,
				type: ESnackbarTypes.error,
			}));
		},
	});

	const {
		mutate: handleDeletePortalFiles,
		isLoading: isLoadingPortalDeleteFiles,
	} = useMutation<IGroupOperationWithActionDTO>({
		mutationKey: [
			DELETE_PORTAL_FILES,
			searchValue,
			folderId,
		],
		enabled: [!!folderId],
		allowedRoles: [EUserRoles.portalProjectAdministrator, EUserRoles.portalProjectMember],
		mutationFn: ({ ids, actionType }: IGroupOperationWithActionDTO) => {
			const filter: IDeletePortalFilesDTO = {
				searchValue,
				folderId,
			};

			return removePortalFiles({
				filter: actionType === EMenuActionsTypes.allFiltered ? filter : { folderId },
				ids,
				isAll: actionType === EMenuActionsTypes.all || actionType === EMenuActionsTypes.allFiltered,
			});
		},
		onSuccess: () => {
			if (closeDeleteConfirm) {
				closeDeleteConfirm();
			}

			dispatch(setSnackbar({
				message: responseMessages.removedPortalFiles,
				type: ESnackbarTypes.success,
			}));

			return queryClient.invalidateQueries([GET_PORTAL_FILES]);
		},
		onError: (err: IResponseError) => {
			if (closeDeleteConfirm) {
				closeDeleteConfirm();
			}

			handleErrorFromMessage(err);
		},
	});

	const {
		mutate: handleDeletePortalFile,
		isLoading: isLoadingDeletePortalFile,
	} = useMutation<string>({
		mutationKey: [DELETE_PORTAL_FILES],
		enabled: [!!folderId],
		allowedRoles: [EUserRoles.portalProjectAdministrator, EUserRoles.portalProjectMember],
		mutationFn: (id: string) => removePortalFiles({
			ids: [id],
			isAll: false,
			filter: null,
		}),
		onSuccess: () => {
			if (closeDeleteConfirm) {
				closeDeleteConfirm();
			}

			dispatch(setSnackbar({
				message: responseMessages.removedPortalFile,
				type: ESnackbarTypes.success,
			}));

			return queryClient.invalidateQueries([GET_PORTAL_FILES]);
		},
		onError: (err: IResponseError) => {
			if (closeDeleteConfirm) {
				closeDeleteConfirm();
			}

			handleErrorFromMessage(err);
		},
	});

	const {
		mutate: handleAttachFilesToMilestones,
		isLoading: isLoadingAttachFilesToMilestones,
	} = useMutation<IGroupOperationAttachPortalDTO>({
		mutationKey: [ATTACH_PORTAL_FILES],
		allowedRoles: [EUserRoles.portalProjectAdministrator, EUserRoles.portalProjectMember],
		mutationFn: ({ ids, actionType, milestoneIds }: IGroupOperationAttachPortalDTO) => {
			const filter = {
				searchValue,
				folderId,
			};

			return attachPortalFiles({
				filter: actionType === EMenuActionsTypes.allFiltered ? filter : { folderId },
				milestoneIds,
				ids,
				isAll: actionType === EMenuActionsTypes.all || actionType === EMenuActionsTypes.allFiltered,
			});
		},
		onSuccess: () => {
			dispatch(setSnackbar({
				message: responseMessages.attachedPortalFiles,
				type: ESnackbarTypes.success,
			}));

			return queryClient.invalidateQueries([GET_PORTAL_FILES]);
		},
		onError: (err: IResponseError) => {
			handleErrorFromMessage(err);
		},
	});

	const {
		mutate: handleAttachFileToMilestones,
		isLoading: isLoadingAttachFileToMilestones,
	} = useMutation<IAttachPortalFileDTO>({
		mutationKey: [ATTACH_PORTAL_FILES],
		allowedRoles: [EUserRoles.portalProjectAdministrator, EUserRoles.portalProjectMember],
		mutationFn: ({ id, milestoneIds }: IAttachPortalFileDTO) => attachPortalFiles({
			ids: [id],
			milestoneIds,
			isAll: false,
			filter: null,
		}),
		onSuccess: (_, variables) => {
			const isMilestoneAttached = variables.milestoneIds.length > 0;

			dispatch(setSnackbar({
				message: isMilestoneAttached
					? responseMessages.attachedPortalFile
					: responseMessages.unassignedPortalFile,
				type: ESnackbarTypes.success,
			}));

			return queryClient.invalidateQueries([GET_PORTAL_FILES]);
		},
		onError: (err: IResponseError) => {
			if (closeDeleteConfirm) {
				closeDeleteConfirm();
			}

			handleErrorFromMessage(err);
		},
	});

	const {
		mutate: handleDownloadPortalFiles,
		isLoading: isLoadingPortalDownloadFiles,
	} = useMutation<IGroupOperationWithActionDTO>({
		mutationKey: [
			DOWNLOAD_PORTAL_FILES,
			searchValue,
			folderId,
		],
		enabled: [!!folderId],
		allowedRoles: [EUserRoles.portalProjectAdministrator, EUserRoles.portalProjectMember],
		mutationFn: ({ ids, actionType }: IGroupOperationWithActionDTO) => {
			const filter: IDownloadPortalFilesDTO = {
				searchValue,
				folderId,
			};

			return downloadPortalFiles({
				filter: actionType === EMenuActionsTypes.allFiltered ? filter : { folderId },
				ids,
				isAll: actionType === EMenuActionsTypes.all || actionType === EMenuActionsTypes.allFiltered,
			});
		},
		onSuccess: () => {
			dispatch(setSnackbar({
				message: responseMessages.apiDocsExported,
				type: ESnackbarTypes.success,
			}));
		},
		onError: () => {
			dispatch(setSnackbar({
				message: responseMessages.apiDocsExportedError,
				type: ESnackbarTypes.error,
			}));
		},
	});

	const {
		mutate: handleDownloadPortalFile,
		isLoading: isLoadingPortalDownloadFile,
	} = useMutation<IDownloadPortalFileDTO>({
		mutationKey: [DOWNLOAD_PORTAL_FILES],
		allowedRoles: [EUserRoles.portalProjectAdministrator, EUserRoles.portalProjectMember],
		mutationFn: ({ fileId }: IDownloadPortalFileDTO) => {
			downloadPortalFile({ fileId })
				.then((data) => {
					if (data?.downloadUrl) {
						fileSaver(data.downloadUrl);
					}
				});
		},
		onSuccess: () => {
			dispatch(setSnackbar({
				message: responseMessages.apiDocsExported,
				type: ESnackbarTypes.success,
			}));
		},
		onError: () => {
			dispatch(setSnackbar({
				message: responseMessages.apiDocsExportedError,
				type: ESnackbarTypes.error,
			}));
		},
	});

	const {
		mutateAsync: moveFiles,
		isLoading: isLoadingMoveFiles,
	} = useMutation<IMovePortalFilesDTO>({
		mutationFn: movePortalFiles,
		mutationKey: [PORTAL_MOVE_FILES],
		onSuccess: (_, variables) => {
			dispatch(setSnackbar({
				message: variables.ids.length > 1
					? responseMessages.movePortalFilesSuccess
					: responseMessages.movePortalFileSuccess,
				type: ESnackbarTypes.success,
			}));

			return [
				queryClient.invalidateQueries([GET_PORTAL_FILES]),
				queryClient.invalidateQueries([GET_PORTAL_FOLDERS]),
			];
		},
		allowedRoles: [EUserRoles.portalProjectAdministrator, EUserRoles.portalProjectMember],
		onError: () => {
			dispatch(setSnackbar({
				message: responseMessages.error,
				type: ESnackbarTypes.error,
			}));
		},
	});

	return {
		uploadFiles,
		isUploadingFiles,
		handleDeletePortalFiles,
		handleDeletePortalFile,
		isLoadingPortalDeleteFiles: isLoadingPortalDeleteFiles || isLoadingDeletePortalFile,
		handleDownloadPortalFiles,
		handleDownloadPortalFile,
		isLoadingPortalDownloadFiles: isLoadingPortalDownloadFiles || isLoadingPortalDownloadFile,
		moveFiles,
		isLoadingMoveFiles,
		handleAttachFilesToMilestones,
		handleAttachFileToMilestones,
		isLoadingAttachFilesToMilestones: isLoadingAttachFilesToMilestones || isLoadingAttachFileToMilestones,
	};
}
